/**
 * -----------------------------------------------------------------------------
 * Geozone Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single geozone of the organization.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLoggedInUserData } from "../../../utils/user";
import { OrganizationEntityListingPayload } from "../../../api/types/Admin";
import { useDebouncedCallback } from "use-debounce";
import HeaderView from "../../../components/admin/headerView";
import { routeUrls } from "../../../navigation/routeUrls";
import { APP_CONFIG } from "../../../constants/constants";
import AppSearchBox from "../../../components/searchBox";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import { useFormik } from "formik";
import Accordian from "../../../components/accordian";
import {
  geozoneDetailsInitialValues,
  geozoneDetailsYupValidationSchema,
  TFormFieldNames,
} from "./validation";
import { GeozoneDetailForm } from "./geozone-form";
import {
  TModalsState,
  setModalsData,
  setMapStateData,
  TListingQueryParams,
  setListingQueryParams,
} from "../../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  useDeleteSingleGeozoneMutation,
  useEditOrganizationGeozoneMutation,
  useOrganizationGeozonesQuery,
  useSingleOrganizationGeozoneQuery,
} from "../../../api/network/adminApiServices";
import { TListData } from "./type";
import {
  EditOrganizationGeozonePayload,
  LegacyGeozoneType,
  OrganizationGeozone,
  TLegacyGeozone,
  TLegacyGeozoneType,
} from "../../../api/types/Geozone";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import { geozoneDescriptionDisplayText } from "../../admins/geozones/geozones";
import { TMapState } from "../../../types/map";
import { getCircleLocs } from "../../../utils/map";
import { formatGeozoneVertices } from "../../../utils/geozonePoints";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import {
  getGeozoneShapeCenter,
  getGeozoneShapeDescription,
} from "../../../utils/geozone";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import {
  DISTANCE_UNIT_VALUES,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../account/constants";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { RESPONSE_CODE } from "../../../api/network/constants";
import svgIcons from "../../../assets/svg/svgIcons";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";
import { convertKmphTo, convertSpeedToKmph } from "../../../utils/distance";

const ScreenAdminDetailGeozone = () => {
  const { geozoneId } = useParams<{ geozoneId: string }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.geozones",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozones.detailsPage",
  });
  const navigate = useNavigate();

  const { getUserACLAccessLevel } = useACL();

  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { geoZones: orgGeozonesQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  // clear map state on unmount - Disabled 6/5 as this is causing issues with mapState when changing screens
  useEffect(() => {
    // return () => {
    //   const restOfMapState = { ...mapState };
    //   delete restOfMapState.mapData;
    //   dispatch(setMapStateData({
    //     ...restOfMapState,
    //     mapData: {
    //       centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
    //     },
    //     pageMapLoaded: false,
    //   }));
    // };
  }, []);

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  // flag to idenfiy if user is coming from create new geozone popup
  const isNewEntity = useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  // prepare query params for fetching organization geozones
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;

  // speed units
  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        geoZones: { ...orgGeozonesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization geozones
  const {
    data: dataOrgGeozones,
    isFetching: isFetchingOrgGeozones,
    error,
  } = useOrganizationGeozonesQuery(orgGeozonesQueryParams, {
    skip: !mapState || typeof mapState.mapScriptLoaded === "undefined",
  });
  const { results, count } = dataOrgGeozones || {};

  // fetch single geozone details
  const {
    data: dataSingleGeozone,
    isFetching: isFetchingSingleGeozone,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationGeozoneQuery(
    {
      account_id: `${thisUserOrganizationId}`,
      geozone_id: `${geozoneId}`,
    },
    {
      skip:
        !thisUserOrganizationId ||
        !geozoneId ||
        !mapState ||
        typeof mapState.mapScriptLoaded === "undefined",
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.geozone_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.geozones);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // geozone mutations
  const [
    editOrganizationGeozoneApiTrigger,
    { isLoading: isLoadingEditGeozone },
  ] = useEditOrganizationGeozoneMutation();
  const [deleteSingleGeozoneApiTrigger, { isLoading: isLoadingDeleteGeozone }] =
    useDeleteSingleGeozoneMutation();

  // prepare list data for geozone list
  const listData: TLegacyGeozone[] = results ?? [];

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: geozoneDetailsInitialValues,
    validationSchema: geozoneDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const preparedUpdateGeozoneData: EditOrganizationGeozonePayload = {
        ...dataSingleGeozone,
        accountid: `${thisUserOrganizationId}`,
        geozoneid: `${geozoneId}`,

        arrivalzone: values.arrivalzone ? 1 : 0,
        arrivalstatuscode: values.arrivalzone ? 61968 : null,
        departurezone: values.departurezone ? 1 : 0,
        departurestatuscode: values.departurezone ? 62000 : null,
        description: values.description,
        displayname: geozoneDescriptionDisplayText(
          `${values.streetaddress}`,
          ""
        ),
        // geocode: values.geocode,
        // group_id: values.assign_group?.map((item: any) => parseInt(item.id)).join(','), // TODO: needs fixing
        // lat_lng: values.lat_lng,
        latitude1:
          parseInt(values.zonetype?.toString() || "0") ===
          LegacyGeozoneType.CIRCLE
            ? mapState?.mapDataForAPIs?.centerPosition?.latitude
            : mapState?.mapData?.locs?.[0]?.latitude || null,
        longitude1:
          parseInt(values.zonetype?.toString() || "0") ===
          LegacyGeozoneType.CIRCLE
            ? mapState?.mapDataForAPIs?.centerPosition?.longitude
            : mapState?.mapData?.locs?.[0]?.longitude || null,
        latitude2: mapState?.mapData?.locs?.[1]?.latitude || null,
        longitude2: mapState?.mapData?.locs?.[1]?.longitude || null,
        latitude3: mapState?.mapData?.locs?.[2]?.latitude || null,
        longitude3: mapState?.mapData?.locs?.[2]?.longitude || null,
        latitude4: mapState?.mapData?.locs?.[3]?.latitude || null,
        longitude4: mapState?.mapData?.locs?.[3]?.longitude || null,
        latitude5: mapState?.mapData?.locs?.[4]?.latitude || null,
        longitude5: mapState?.mapData?.locs?.[4]?.longitude || null,
        latitude6: mapState?.mapData?.locs?.[5]?.latitude || null,
        longitude6: mapState?.mapData?.locs?.[5]?.longitude || null,
        latitude7: mapState?.mapData?.locs?.[6]?.latitude || null,
        longitude7: mapState?.mapData?.locs?.[6]?.longitude || null,
        latitude8: mapState?.mapData?.locs?.[7]?.latitude || null,
        longitude8: mapState?.mapData?.locs?.[7]?.longitude || null,
        latitude9: mapState?.mapData?.locs?.[8]?.latitude || null,
        longitude9: mapState?.mapData?.locs?.[8]?.longitude || null,
        latitude10: mapState?.mapData?.locs?.[9]?.latitude || null,
        longitude10: mapState?.mapData?.locs?.[9]?.longitude || null,
        // points beyond the 10 columns; always sent so a stale value is never kept
        vertices:
          parseInt(values.zonetype?.toString() || "0") ===
          LegacyGeozoneType.CIRCLE
            ? null
            : formatGeozoneVertices(mapState?.mapData?.locs ?? []),
        //values.maxlatitude || mapState?.mapDataForAPIs?.centerPosition?.latitude,
        maxlatitude:
          values.zonetype === LegacyGeozoneType.CIRCLE &&
          !!mapState?.mapDataForAPIs?.centerPosition?.latitude &&
          !!mapState?.mapDataForAPIs?.radius
            ? mapState?.mapDataForAPIs?.centerPosition?.latitude +
              mapState?.mapDataForAPIs?.radius / 111000 // Approximate conversion from meters to degrees
            : mapState?.mapData?.locs
                ?.filter((loc) => !!loc)
                ?.reduce(
                  (acc: number, curr: any) => Math.max(acc, curr.latitude),
                  -90
                ),
        //values.maxlongitude || mapState?.mapDataForAPIs?.centerPosition?.longitude,
        maxlongitude:
          values.zonetype === LegacyGeozoneType.CIRCLE &&
          !!mapState?.mapDataForAPIs?.centerPosition?.longitude &&
          !!mapState?.mapDataForAPIs?.radius
            ? mapState?.mapDataForAPIs?.centerPosition?.longitude +
              mapState?.mapDataForAPIs?.radius / 111000 // Approximate conversion from meters to degrees
            : mapState?.mapData?.locs
                ?.filter((loc) => !!loc)
                ?.reduce(
                  (acc: number, curr: any) => Math.max(acc, curr.longitude),
                  -180
                ),
        //values.minlatitude || mapState?.mapDataForAPIs?.centerPosition?.latitude,
        minlatitude:
          values.zonetype === LegacyGeozoneType.CIRCLE &&
          !!mapState?.mapDataForAPIs?.centerPosition?.latitude &&
          !!mapState?.mapDataForAPIs?.radius
            ? mapState?.mapDataForAPIs?.centerPosition?.latitude -
              mapState?.mapDataForAPIs?.radius / 111000 // Approximate conversion from meters to degrees
            : mapState?.mapData?.locs
                ?.filter((loc) => !!loc)
                ?.reduce(
                  (acc: number, curr: any) => Math.min(acc, curr.latitude),
                  90
                ),
        //values.minlongitude || mapState?.mapDataForAPIs?.centerPosition?.longitude,
        minlongitude:
          values.zonetype === LegacyGeozoneType.CIRCLE &&
          !!mapState?.mapDataForAPIs?.centerPosition?.longitude &&
          !!mapState?.mapDataForAPIs?.radius
            ? mapState?.mapDataForAPIs?.centerPosition?.longitude -
              mapState?.mapDataForAPIs?.radius / 111000 // Approximate conversion from meters to degrees
            : mapState?.mapData?.locs
                ?.filter((loc) => !!loc)
                ?.reduce(
                  (acc: number, curr: any) => Math.min(acc, curr.longitude),
                  180
                ),
        priority: values.priority,
        // properties: mapState?.mapDataForAPIs ?? values.properties,
        radius: mapState?.mapDataForAPIs?.radius || 0,
        reversegeocode: values.reversegeocode ? 1 : 0,
        shapecolor: values.shapecolor,
        speedlimitkph: convertSpeedToKmph(speedUnit, values.speedlimitkph || 0),
        streetaddress: geozoneDescriptionDisplayText(
          `${values.streetaddress}`,
          ""
        ),
        // stateprovince: values.stateprovince, // historical data suggests 2-character state code, but Bing optionally provides, say, either 'Ohio' or 'OH'
        zonetype: values.zonetype,
      };

      if (
        preparedUpdateGeozoneData.latitude1 === null ||
        preparedUpdateGeozoneData.longitude1 === null
      ) {
        toast.error(t("map.data_incomplete"));
        return;
      }

      // call api for updating geozone
      editOrganizationGeozoneApiTrigger(preparedUpdateGeozoneData)
        .unwrap()
        .then(() => {
          toast.success(t("toast.geozone_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.geozones);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (isFetchingSingleGeozone) {
      setFormikValuesReady(false);
    } else if (!!dataSingleGeozone) {
      const Microsoft = (window as any).Microsoft;
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        // properties:
        //   Object.keys(dataSingleGeozone?.properties).length > 0
        //   ? dataSingleGeozone?.properties
        //   // set default values for properties - to be used by map when user is creating new geozone
        //   : {
        //     ...(
        //       dataSingleGeozone?.zonetype === "Route" ? {
        //         centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        //       } : (dataSingleGeozone?.zonetype === "Polygon" ? {
        //         centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        //         locs:
        //           getCircleLocs(
        //             new Microsoft.Maps.Location(APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude, APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude),
        //             APP_CONFIG.MAPS.DEFAULTS.RADIUS,
        //             APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
        //             true
        //           ).map((loc: any) => ({ latitude: loc.latitude, longitude: loc.longitude }))
        //       } : {
        //         centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        //         radius: APP_CONFIG.MAPS.DEFAULTS.RADIUS,
        //       })
        //     )
        //   },
        ...dataSingleGeozone,
        ...(isNewEntity?.current
          ? {
              centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
              ...(dataSingleGeozone?.zonetype.toString() ===
              LegacyGeozoneType.ROUTE.toString()
                ? {
                    // new route
                    latitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
                    longitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude,
                    radius: APP_CONFIG.MAPS.DEFAULTS.RADIUS_CORRIDOR,
                  }
                : dataSingleGeozone?.zonetype.toString() ===
                    LegacyGeozoneType.POLYGON.toString()
                  ? {
                      // new polygon
                      ...[
                        {
                          circleLocs: getCircleLocs(
                            new google.maps.LatLng(
                              APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
                              APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude
                            ),
                            APP_CONFIG.MAPS.DEFAULTS.RADIUS,
                            APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
                            true
                          ),
                        },
                      ].reduce(
                        (
                          acc: any,
                          curr: { circleLocs: { lat: number; lng: number }[] },
                          i: number
                        ) => {
                          const bounds = new google.maps.LatLngBounds();
                          curr.circleLocs.forEach((point: any, i: number) => {
                            acc[`latitude${i + 1}`] = point.lat();
                            acc[`longitude${i + 1}`] = point.lng();
                            bounds.extend({
                              lat: point.lat(),
                              lng: point.lng(),
                            });
                          });
                          acc[`minlatitude`] = bounds.getNorthEast().lat();
                          acc[`minlongitude`] = bounds.getNorthEast().lng();

                          acc[`maxlatitude`] = bounds.getSouthWest().lat();
                          acc[`maxlongitude`] = bounds.getSouthWest().lng();
                          return acc;
                        },
                        {}
                      ),
                    }
                  : {
                      // new circle
                      latitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.latitude,
                      longitude1: APP_CONFIG.MAPS.DEFAULTS.CENTER.longitude,
                      radius:
                        dataSingleGeozone?.radius ||
                        APP_CONFIG.MAPS.DEFAULTS.RADIUS,
                    }),
            }
          : {}),
        description: dataSingleGeozone?.description || "",
        // city: dataSingleGeozone?.city || "",
        zonetype: dataSingleGeozone?.zonetype,
        // geocode: "", // dataSingleGeozone?.geocode || "",
        // lat_lng: `${getGeozoneShapeCenter(dataSingleGeozone)}`,
        priority:
          dataSingleGeozone?.priority || geozoneDetailsInitialValues.priority,
        // groupid: dataSingleGeozone?.groupid || "",
        // assign_group: dataSingleGeozone?.groups.map(item => ({ value: `${item.id}`, label: item.name })) || [],
        reversegeocode: dataSingleGeozone?.reversegeocode === 1,
        arrivalzone: dataSingleGeozone?.arrivalzone === 1,
        departurezone: dataSingleGeozone?.departurezone === 1,
        shapecolor:
          dataSingleGeozone?.shapecolor ||
          geozoneDetailsInitialValues.shapecolor,
        speedlimitkph: convertKmphTo(
          speedUnit,
          dataSingleGeozone?.speedlimitkph ||
            geozoneDetailsInitialValues.speedlimitkph ||
            0
        )
          .toFixed(2)
          .replace(".00", ""),
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [isFetchingSingleGeozone, dataSingleGeozone, geozoneId]);

  useEffect(() => {
    if (!!userCanEdit) {
      setTimeout(() => {
        formik.setFieldTouched("radius", true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [userCanEdit]);

  // handle edit geozone
  const handleEditGeozone = () => {
    if (userCanEdit) {
      const checkValue = mapState?.mapDataForAPIs; // ?? values.properties;
      switch (parseInt((values.zonetype || "")?.toString())) {
        case LegacyGeozoneType.ROUTE:
          if (!checkValue?.locs || checkValue?.locs.length < 2) {
            toast.error(t("toast.route_points_required"));
            return;
          }
          break;
        case LegacyGeozoneType.POLYGON:
          if (!checkValue?.locs || checkValue?.locs.length < 3) {
            toast.error(t("toast.polygon_points_required"));
            return;
          }
          break;
        case LegacyGeozoneType.CIRCLE:
        default:
          if (!checkValue?.centerPosition || !checkValue?.radius) {
            toast.error(t("toast.circle_points_required"));
            return;
          }
          break;
      }
      formik.handleSubmit();
    } else {
      toast.error(t("toast.edit_not_allowed"));
    }
  };

  // handle delete geozone
  const handleDeleteGeozone = () => {
    deleteSingleGeozoneApiTrigger({
      account_id: `${thisUserOrganizationId}`,
      geozone_id: `${geozoneId}`,
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.geozone_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.geozones);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.adminChildren.geozones);

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "danger",
              label: tMain("delete"),
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteConfirmation: true,
                  })
                );
              },
              disabled:
                isFetchingSingleGeozone ||
                isLoadingEditGeozone ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) <
                  ROLE_ACCESS_LEVELS.ALL,
            } as FormActionButton,

            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                    variant: "secondary",
                  } as FormActionButton,
                ]
              : []),

            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditGeozone
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleGeozone ||
                isLoadingEditGeozone ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) <
                  ROLE_ACCESS_LEVELS.WRITE,
            } as FormActionButton,
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleGeozone,
    isLoadingEditGeozone,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: handleEditGeozone,
              disabled: isLoadingEditGeozone || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditGeozone, errors]);

  return (
    <>
      <HeaderView
        title={`${t("heading")}`} // ${dataSingleGeozone?.zone_id ? `: ${dataSingleGeozone?.zone_id}` : ''}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="grid grid-cols-12 gap-4 p-4 text-sm">
          {/* <div className="col col-span-3 max-h-60 overflow-auto"><h3><strong>values.properties</strong></h3><pre className="text-xs">{JSON.stringify(values.properties, undefined, 2)}</pre></div> */}
          <div className="col col-span-3 max-h-60 overflow-auto">
            <h3>
              <strong>[mapState] Map Center</strong>
            </h3>
            <pre className="text-xs">
              {JSON.stringify(mapState?.mapCenter, undefined, 2)}
            </pre>
          </div>
          <div className="col col-span-3 max-h-60 overflow-auto">
            <h3>
              <strong>[mapState] Map Data</strong>
            </h3>
            <pre className="text-xs">
              {JSON.stringify(mapState?.mapData, undefined, 2)}
            </pre>
          </div>
          <div className="col col-span-3 max-h-60 overflow-auto">
            <h3>
              <strong>[mapState] Map Data For APIs</strong>
            </h3>
            <pre className="text-xs">
              {JSON.stringify(mapState?.mapDataForAPIs, undefined, 2)}
            </pre>
          </div>
          <div className="col col-span-3 max-h-60 overflow-auto">
            <h3>
              <strong>Formik values</strong>
            </h3>
            <pre className="text-xs">
              {JSON.stringify(values, undefined, 2)}
            </pre>
          </div>
        </div>
      )}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgGeozones ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgGeozonesQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {listData?.map((item, index: number) => (
                <AdminListingColumnItem
                  key={index}
                  selected={geozoneId === item.geozoneid}
                  onClick={() =>
                    navigate(
                      `${routeUrls.dashboardChildren.adminChildren.geozones}/${item.geozoneid}`
                    )
                  }
                  title={`${item.geozoneid}`}
                  description={`${item.streetaddress || item.description}`}
                  asideText={
                    getGeozoneShapeDescription(item, orgDistanceUnit) ?? "-"
                  }
                  bottomText={geozoneDescriptionDisplayText(
                    `${item.description}`,
                    "-"
                  )}
                />
              ))}
            </div>
            {!isFetchingOrgGeozones && (
              <Pagination
                pageSize={orgGeozonesQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      geoZones: {
                        ...orgGeozonesQueryParams,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgGeozonesQueryParams.page_size)
                    : 1
                }
                forcePage={orgGeozonesQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      geoZones: {
                        ...orgGeozonesQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleGeozone && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditGeozone}
                      disabled={isLoadingEditGeozone || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleGeozone && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isFetchingSingleGeozone || isLoadingEditGeozone || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) < ROLE_ACCESS_LEVELS.ALL}
                    />
                  </div> */}
                  {/* {userCanEdit && (<div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="secondary"
                      label={tMain("cancel")}
                      onClick={onBackButtonClick}
                    />
                  </div>)} */}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditGeozone : () => setUserCanEdit(!userCanEdit)}
                      disabled={isFetchingSingleGeozone || isLoadingEditGeozone || (userCanEdit && !!Object.keys(errors).length) || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleGeozone || isLoadingEditGeozone || isLoadingDeleteGeozone ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_details")}
                  openByDefault={true}
                  collapsible={false}
                >
                  <GeozoneDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleGeozone ||
                      !formikValuesReady ||
                      !mapState ||
                      typeof mapState.mapScriptLoaded === "undefined"
                    }
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteGeozone} />
    </>
  );
};

export default ScreenAdminDetailGeozone;
